import { Navigate, useRoutes } from "react-router-dom";

// Auth guards
import AuthGuard from "../auth/AuthGuard";
import GuestGuard from "../auth/GuestGuard";

// layouts
import CompactLayout from "../layouts/compact";
import DashboardLayout from "../layouts/dashboard";

import {
  LoginPage,
  DashboardPage,
  Page500,
  Page404,
  ApplicationsPage,
  YearPage,
  GradePage,
  LearningCentrePage,
  NewStaffPage,
  DepartmentPage,
  AllStaffPage,
  UpdateStaffPage,
  ViewStaffPage,
  PaceOrderPage,
  StoreListPage,
  ParentViewPage,
  ParentsListPage,
  NewParentPage,
  EditParentPage,
  NewStudentsPage,
  AllStudentsPage,
  ViewStudentPage,
  UpdateStudentPage,
  ClassListsPage,
  ClassViewPage,
  SubjectsPage,
  OtherSubjectsPage,
  PaceListPage,
  NewPacePage,
  EditPacePage,
  ProjectionsPage,
  LcListPage,
  StudentListPage,
  SubjectListPage,
  StudentProjectionsPage,
  LCProjectionItemsPage,
  SubjectProjectionsPage,
  PaceInventoryPage,
  PaceOrderItemsPage,
  ReceivePacesPage,
  StaffAttendance,
  TrackingLcListPage,
  TrackingStudentsPage,
  TrackingPaceListPage,
  TempStaffPage,
  CheckinsPage,
  ActivityClassListPage,
  ActivityPage,
  ActivityTermsPage,
  PaceBillingTermPage,
  PaceBillingPage,
  PaceBillingListItemsPage,
} from "./elements";

export default function Router() {
  return useRoutes([
    // Auth
    {
      path: "auth",
      children: [
        {
          path: "login",
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
      ],
    },

    // Dashboard
    {
      path: "dashboard",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to="/dashboard/app" replace />, index: true },
        { path: "app", element: <DashboardPage /> },
        {
          path: "students",
          children: [
            { path: "applications", element: <ApplicationsPage /> },
            { path: "new", element: <NewStudentsPage /> },
            { path: "list", element: <AllStudentsPage /> },
            { path: "view/:studentid", element: <ViewStudentPage /> },
            { path: "update/:studentid", element: <UpdateStudentPage /> },
          ],
        },
        {
          path: "metadata",
          children: [
            {
              element: <Navigate to="/dashboard/metadata/year" replace />,
              index: true,
            },
            { path: "year", element: <YearPage /> },
            { path: "grades", element: <GradePage /> },
            { path: "learningcentres", element: <LearningCentrePage /> },
            { path: "departments", element: <DepartmentPage /> },
          ],
        },
        {
          path: "staff",
          children: [
            {
              element: <Navigate to="/dashboard/staff/list" replace />,
              index: true,
            },
            { path: "new", element: <NewStaffPage /> },
            { path: "list", element: <AllStaffPage /> },
            { path: "update/:staffid", element: <UpdateStaffPage /> },
            { path: "view/:staffid", element: <ViewStaffPage /> },
            { path: "attendance", element: <StaffAttendance /> },
            { path: "temp", element: <TempStaffPage /> },
          ],
        },
        {
          path: "parents",
          children: [
            {
              element: <Navigate to="/dashboard/parents/list" replace />,
              index: true,
            },
            { path: "list", element: <ParentsListPage /> },
            { path: "new", element: <NewParentPage /> },
            { path: "view/:parentid", element: <ParentViewPage /> },
            { path: "edit/:parentid", element: <EditParentPage /> },
          ],
        },
        {
          path: "finance",
          children: [
            {
              element: (
                <Navigate to="/dashboard/finance/pace-billing" replace />
              ),
              index: true,
            },
            { path: "pace-billing", element: <PaceBillingTermPage /> },
            {
              path: "pace-billing/term/:termId",
              element: <PaceBillingPage />,
            },
            {
              path: "pace-billing/list/:listId",
              element: <PaceBillingListItemsPage />,
            },
          ],
        },
        {
          path: "activities",
          children: [
            {
              element: <Navigate to="/dashboard/activities/list" replace />,
              index: true,
            },
            { path: "list", element: <ActivityPage /> },
            { path: "terms/:termId", element: <ActivityClassListPage /> },
            {
              path: "terms/:termId/classlist/:classId",
              element: <CheckinsPage />,
            },
            { path: "checkins", element: <ActivityTermsPage /> },
          ],
        },
        {
          path: "classlist",
          children: [
            {
              element: <Navigate to="/dashboard/classlist/list" replace />,
              index: true,
            },
            { path: "list", element: <ClassListsPage /> },
            { path: "view/:classlistid", element: <ClassViewPage /> },
          ],
        },
        {
          path: "subjects",
          children: [
            {
              element: <Navigate to="/dashboard/subjects/list" replace />,
              index: true,
            },
            { path: "list", element: <SubjectsPage /> },
            { path: "other", element: <OtherSubjectsPage /> },
          ],
        },
        {
          path: "projections",
          children: [
            {
              element: <Navigate to="/dashboard/projections/list" replace />,
              index: true,
            },
            { path: "list", element: <ProjectionsPage /> },
            { path: "list/:projectionid/lc", element: <LcListPage /> },
            {
              path: "list/:projectionid/students",
              element: <StudentListPage />,
            },
            {
              path: "list/:projectionid/subjects",
              element: <SubjectListPage />,
            },
            {
              path: "list/:projectionid/students/:studentid",
              element: <StudentProjectionsPage />,
            },
            {
              path: "list/:projectionid/lc/:lcid",
              element: <LCProjectionItemsPage />,
            },
            {
              path: "list/:projectionid/subjects/:subjectid",
              element: <SubjectProjectionsPage />,
            },
            {
              path: "paceorder",
              children: [
                { element: <PaceOrderPage />, index: true },

                { path: ":orderid", element: <PaceOrderItemsPage /> },
                { path: "receive/:orderid", element: <ReceivePacesPage /> },
              ],
            },
          ],
        },
        {
          path: "paces",
          children: [
            {
              element: <Navigate to="/dashboard/paces/list" replace />,
              index: true,
            },
            { path: "list", element: <PaceListPage /> },
            { path: "new", element: <NewPacePage /> },
            { path: "edit/:paceid", element: <EditPacePage /> },
            { path: "store", element: <StoreListPage /> },
            { path: "inventory", element: <PaceInventoryPage /> },
          ],
        },

        {
          path: "tracking",
          children: [
            {
              element: <Navigate to="/dashboard/tracking/lc" replace />,
              index: true,
            },
            { path: "lc", element: <TrackingLcListPage /> },
            {
              path: "lc/:lcId",
              element: <TrackingStudentsPage />,
            },
            {
              path: "student/:studentid",
              element: <TrackingPaceListPage />,
            },
          ],
        },

        //     // { path: 'home', element: <GeneralAppPage /> },
      ],
    },

    {
      element: <CompactLayout />,
      children: [
        { path: "500", element: <Page500 /> },
        { path: "404", element: <Page404 /> },
      ],
    },
    { path: "*", element: <Navigate to="/404" replace /> },
    { path: "/", element: <Navigate to="/dashboard" replace /> },
  ]);
}
